import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Minus, ArrowRight } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const VS = {
  fieldwire: {
    slug: "send-proof-vs-fieldwire",
    them: "Fieldwire",
    title: "Send-Proof vs Fieldwire — punch lists & photo proof",
    desc: "Send-Proof vs Fieldwire: how a $9.99 flat-price punch list with no-login share links and open tracking compares to Fieldwire's per-seat field-management plans.",
    h1: "Send-Proof vs Fieldwire",
    definition: "Send-Proof is a punch list app: you text your crew a link, get a photo on every item, and see when it opens. Here's how it compares to Fieldwire for the punch-list job.",
    rows: [
      { label: "Price", sp: "$9.99/mo flat — everyone you send to is free", them: "Per-user plans; paid tiers charge per seat" },
      { label: "Crew access", sp: "Open a link — no app, no login", them: "Crew installs the app and signs in" },
      { label: "Open tracking", sp: "See the moment your link is opened", them: "Not a focus" },
      { label: "Setup", sp: "Send a text — you're done", them: "Project setup and onboarding" },
      { label: "Best for", sp: "Fast punch lists with photo proof", them: "Full field-management platform" },
    ],
    footnote: "Already run Fieldwire? Keep it — add Send-Proof for the quick punch list your subs will actually use, with no new logins.",
    faqs: [
      { q: "Is Send-Proof a replacement for Fieldwire?", a: "Not necessarily. Many crews keep Fieldwire for full project management and add Send-Proof for the one thing that should be dead simple — a punch list you text, with photo proof back." },
      { q: "Why is Send-Proof cheaper?", a: "It's one flat $9.99/month for you. The crew and clients you send lists to never pay or create an account, so the price stays $9.99 no matter how many people you send to." },
    ],
  },
  groupTexts: {
    slug: "send-proof-vs-group-texts",
    them: "group texts & spreadsheets",
    title: "Send-Proof vs group texts & spreadsheets",
    desc: "Send-Proof vs group texts and spreadsheets: structured per-trade punch lists, timestamped photo proof, and open tracking instead of buried threads and screenshots.",
    h1: "Send-Proof vs group texts & spreadsheets",
    definition: "Send-Proof is a punch list app: you text your crew a link and get photo proof back on every item. Here's how that compares to running punch lists over group texts and spreadsheets.",
    rows: [
      { label: "Organization", sp: "One list per trade, in order", them: "Jumbled thread — scroll to find it" },
      { label: "Proof", sp: "Timestamped photo on each item", them: "Photos scattered in the camera roll" },
      { label: "Status", sp: "See when the link is opened", them: "\"Did you get it?\" texts" },
      { label: "Record", sp: "A clean, shareable report link", them: "Screenshots and copy-paste" },
      { label: "Crew effort", sp: "Tap a link, snap a photo", them: "Remember what to send and where" },
    ],
    footnote: "No app for your crew and nothing new to learn — if they can reply to a text, they can work a Send-Proof list.",
    faqs: [
      { q: "Do my crew members need an app?", a: "No. You send a link by text — they tap it, work the list, and snap a photo on each item. Nothing to install, no account to create." },
      { q: "What do I get that a group text can't do?", a: "A per-trade list in order, a timestamped photo on every item, a live signal when your link is opened, and one clean report link instead of a scattered thread." },
    ],
  },
};

export default function Compare({ variant = "fieldwire" }) {
  const navigate = useNavigate();
  const go = () => navigate("/auth?mode=signup");
  const v = VS[variant] || VS.fieldwire;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${v.title} | Send-Proof`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", v.desc);
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-compare", "1");
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: v.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
    document.head.appendChild(s);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
      s.remove();
    };
  }, [v]);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-card text-foreground">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[600px]">
        <div className="absolute left-1/2 top-[-200px] h-[440px] w-[760px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />
      </div>
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-[hsl(222,46%,10%)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="compare-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-8 w-8 rounded-lg" />
            <span className="font-heading text-lg font-bold tracking-tight">Send-Proof</span>
          </Link>
          <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="compare-nav-cta" onClick={go}>Start free trial</Button>
          </div>
        </header>

        <motion.section variants={reveal} initial="hidden" animate="show" className="mx-auto max-w-2xl px-5 pt-10 text-center sm:px-8 sm:pt-16" data-testid="compare-page">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">{v.h1}</h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground sm:text-lg" data-testid="compare-definition">{v.definition}</p>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-3xl px-5 pt-10 sm:px-8">
          <div className="overflow-hidden rounded-2xl border border-white/5 ring-1 ring-white/5" data-testid="compare-table">
            <div className="grid grid-cols-3 bg-[hsl(222,44%,13%)] text-sm font-bold">
              <div className="p-4" />
              <div className="p-4 text-emerald-400">Send-Proof</div>
              <div className="p-4 text-muted-foreground">{v.them}</div>
            </div>
            {v.rows.map((r, i) => (
              <div key={r.label} className={`grid grid-cols-3 text-sm ${i % 2 ? "bg-[hsl(222,46%,10%)]" : "bg-[hsl(222,44%,12%)]"}`}>
                <div className="p-4 font-semibold">{r.label}</div>
                <div className="flex items-start gap-2 p-4"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /><span>{r.sp}</span></div>
                <div className="flex items-start gap-2 p-4 text-muted-foreground"><Minus className="mt-0.5 h-4 w-4 shrink-0" /><span>{r.them}</span></div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-center text-sm text-muted-foreground">{v.footnote}</p>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-2xl px-5 pt-12 sm:px-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight">Common questions</h2>
          <div className="mt-4 space-y-3">
            {v.faqs.map((f) => (
              <div key={f.q} className="rounded-xl border border-white/5 bg-[hsl(222,44%,12%)] p-5">
                <div className="font-semibold">{f.q}</div>
                <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-2xl px-5 py-14 text-center sm:px-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Try it on one job — free</h2>
          <p className="mx-auto mt-2 max-w-md text-base text-muted-foreground">15 days free, no credit card. $9.99/month after — one flat plan.</p>
          <Button size="lg" className="mt-6 h-12 bg-emerald-500 px-7 text-base hover:bg-emerald-600" data-testid="compare-cta" onClick={go}>
            Start 15-day free trial <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </motion.section>

        <SiteFooter />
      </div>
    </div>
  );
}
